import { styled } from '@mui/material';

import { useMemo } from 'react';

import { useNavigate } from 'react-router';

import {
  Box,
  Button,
  ButtonsContainer,
  Modal,
  Typography,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { ParticipantsIcon } from '@app-velocity/icons';
import { TCurrentUserTeamDto } from '@app-velocity/av-api-v2';

const StyledDescription = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  maxWidth: theme.spacing(100),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  marginBottom: theme.spacing(2),
  textAlign: 'center',
}));

const StyledText = styled(Typography)({
  color: 'var(--text-text-secondary-color)',
  whiteSpace: 'pre-line',
});

const IconContainer = styled('div')({
  textAlign: 'center',
  svg: {
    stroke: 'var(--highlight-color)',
  },
});

const isPaidTeamAvailableModalData = (
  data: unknown
): data is {
  team: TCurrentUserTeamDto & { workspaceId?: string };
  redirectToSelectFlow?: boolean;
} => {
  const castedData = data as {
    team: TCurrentUserTeamDto & { workspaceId?: string };
    redirectToSelectFlow?: boolean;
  };

  return !!castedData.team;
};

export const PaidTeamAvailableModalContent = () => {
  const { t } = useI18n();
  const { modalData, openedModalId, closeModal } = useModalWrapperContext();

  const navigate = useNavigate();

  const { team, redirectToSelectFlow } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isPaidTeamAvailableModalData(currentModalData)) {
      return currentModalData;
    }

    return { team: null, redirectToSelectFlow: null };
  }, [modalData, openedModalId]);

  const handleTeamSwitch = () => {
    if (!team) return;

    if (team.workspaceId && redirectToSelectFlow) {
      navigate(`/${team.teamId}/${team.workspaceId}/prototypes/new`);
    } else navigate(`/${team.teamId}`);
    closeModal();
  };

  return (
    <Modal>
      <Modal.Header />
      <Modal.Content>
        <IconContainer>
          <ParticipantsIcon />
        </IconContainer>
        <StyledDescription>
          <StyledTitle variant="header" component="h2">
            {t('teams:team_notification.title')}
          </StyledTitle>
          <StyledText variant="body2">
            {!redirectToSelectFlow
              ? t('teams:team_notification.start_up.single_paid_team', {
                  name: team?.teamName,
                })
              : t('teams:team_notification.test_a_flow.single_paid_team', {
                  name: team?.teamName,
                })}
          </StyledText>
        </StyledDescription>
        <ButtonsContainer direction="vertical">
          <Button variant="cta" onClick={handleTeamSwitch}>
            {!redirectToSelectFlow
              ? t('teams:team_notification.start_up.select_team', {
                  name: team?.teamName,
                })
              : t('teams:team_notification.test_a_flow.select_team', {
                  name: team?.teamName,
                })}
          </Button>
        </ButtonsContainer>
      </Modal.Content>
    </Modal>
  );
};
